import type { SupabaseClient } from '@supabase/supabase-js'
import { useRouter } from 'next/navigation'
import type { NotificationMessage, NotificationAction } from '@/types/notification'
import {
  handleTeamInviteAction,
  handleTeamProposalAction,
  handleEstimateNotificationAction,
  handleTeamJoinRequestAction,
} from '@/utils/notification-handlers'
import { getSuccessMessage } from '@/utils/notification-helpers'
import { toast } from '@/hooks/use-toast'

export const handleLogout = async (
  supabase: SupabaseClient | null,
  router: ReturnType<typeof useRouter>
) => {
  if (!supabase) return
  // 완전한 로그아웃: 모든 스토리지 정리
  await supabase.auth.signOut({ scope: 'global' })
  
  // localStorage와 sessionStorage 정리
  if (typeof window !== 'undefined') {
    // Supabase 관련 키 정리
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth')) {
        localStorage.removeItem(key)
      }
    })
    
    // sessionStorage도 정리
    const sessionKeys = Object.keys(sessionStorage)
    sessionKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth') || key === 'profileType') {
        sessionStorage.removeItem(key)
      }
    })
  }
  
  router.push('/')
  window.location.reload()
}

export const handleMessageAction = async (
  message: NotificationMessage,
  action: NotificationAction,
  supabase: SupabaseClient,
  setRecentMessages: (updater: (prev: NotificationMessage[]) => NotificationMessage[]) => void,
  setUnreadCount: (updater: (prev: number) => number) => void,
  setProcessingMessageId: (id: string | number | null) => void,
  loadNotifications: () => Promise<void>
) => {
  const messageId = message.id
  // 처리 중인 메시지 표시
  setProcessingMessageId(messageId)

  try {
    // 1단계: 알림 타입별로 헬퍼 함수 호출
    switch (message.type) {
      case 'team_invite':
        await handleTeamInviteAction(supabase, message, action)
        break

      case 'team_proposal':
        await handleTeamProposalAction(message, action)
        break

      case 'team_join_request':
      case 'join_request':
        await handleTeamJoinRequestAction(supabase, message, action)
        break

      case 'client_to_team_estimate_request':
      case 'team_to_client_estimate_request':
      case 'client_to_individual_estimate_request':
      case 'estimate_request':
      case 'estimate_submitted':
      case 'estimate_accepted':
        await handleEstimateNotificationAction(message, action)
        break

      default:
        console.warn('[handleMessageAction] 처리되지 않은 타입:', message.type)
        throw new Error(`처리할 수 없는 알림 타입입니다: ${message.type}`)
    }

    // 2단계: DB 업데이트 완료 후 로컬 상태 업데이트
    const successMessage = getSuccessMessage(message.type, action)

    // 즉시 로컬 상태에서 제거 (optimistic update)
    setRecentMessages(prev => prev.filter(m => m.id !== messageId))
    setUnreadCount(prev => Math.max(0, prev - 1))
    setProcessingMessageId(null)

    toast({
      title: successMessage,
    })

    // 3단계: DB 업데이트 완료 후 백그라운드에서 목록 새로고침
    loadNotifications().catch(console.error)
  } catch (error) {
    console.error('메시지 처리 실패:', error)
    // 에러 발생 시 처리 상태 해제
    setProcessingMessageId(null)
    
    // redirectTo가 있으면 해당 경로로 이동
    const errorWithRedirect = error as Error & { redirectTo?: string }
    if (errorWithRedirect.redirectTo && typeof window !== 'undefined') {
      toast({
        variant: 'default',
        title: errorWithRedirect.message || '처리에 실패했습니다',
        description: '해당 페이지로 이동합니다.',
      })
      // 약간의 딜레이 후 이동 (토스트가 보이도록)
      setTimeout(() => {
        window.location.href = errorWithRedirect.redirectTo!
      }, 1000)
      return
    }

    const errorMessage = error instanceof Error ? error.message : '잠시 후 다시 시도해 주세요.'
    toast({
      variant: 'destructive',
      title: '처리에 실패했습니다',
      description: errorMessage,
    })
  }
}
